/**
 * PassportPage
 *
 * A traveler's visual passport — every tour they've completed becomes a stamp.
 * Design: warm postcard/vintage aesthetic. Stamps are dense but detailed cards
 * with the tour's cover image, city name, times-completed badge, and the date
 * of the first completion.
 *
 * Two entry points:
 *  - /passport/me     — the signed-in user's own passport (auth-required)
 *  - /passport/user/:userId  — public read (403 if that profile is private)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, MapPin, Flame, Share2, BookOpen, Trophy, Compass, Globe, Download, X, Footprints, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { generatePassportCard, sharePassportCard } from '../lib/passportCard';
import { formatWalkedDistance, formatWalkedDuration } from '../lib/walkingStats';
import CitiesExploredMap from '../components/CitiesExploredMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return ''; }
};

const initials = (name) =>
  (name || '?').split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const PassportPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { token, user } = useAuth();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cardPreview, setCardPreview] = useState(null); // {blobUrl, blob} or null
  const [generating, setGenerating] = useState(false);
  const [savingStreak, setSavingStreak] = useState(false);

  const isOwnPassport = !userId || (user && user.user_id === userId);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = isOwnPassport ? `${API}/passport/me` : `${API}/passport/user/${userId}`;
      const headers = isOwnPassport && token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(url, { headers });
      setPassport(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load passport');
    } finally {
      setLoading(false);
    }
  }, [isOwnPassport, userId, token]);

  useEffect(() => { load(); }, [load]);

  // Auth gate for own passport
  useEffect(() => {
    if (!token && isOwnPassport) {
      navigate('/', { replace: true });
    }
  }, [token, isOwnPassport, navigate]);

  const handleShare = async () => {
    if (!passport) return;
    if ((passport.totals.unique_tours || 0) === 0) {
      toast.info('Complete your first tour to earn a stamp before sharing!');
      return;
    }
    setGenerating(true);
    try {
      const blob = await generatePassportCard(passport);
      const url = URL.createObjectURL(blob);
      setCardPreview({ blobUrl: url, blob });
    } catch (err) {
      toast.error('Could not generate passport card. Try again in a sec.');
    } finally {
      setGenerating(false);
    }
  };

  const closeCardPreview = () => {
    if (cardPreview?.blobUrl) URL.revokeObjectURL(cardPreview.blobUrl);
    setCardPreview(null);
  };

  const doShare = async () => {
    if (!passport) return;
    setGenerating(true);
    try {
      const result = await sharePassportCard(passport, `${passport.user.display_name.replace(/\s+/g, '-').toLowerCase()}-audiostride-passport`);
      if (result.method === 'download') {
        toast.success('Passport card downloaded — post it wherever!');
      } else if (result.method === 'share') {
        toast.success('Shared!');
      }
      closeCardPreview();
    } catch (err) {
      toast.error('Share failed');
    } finally {
      setGenerating(false);
    }
  };

  const doCopyLink = async () => {
    if (!passport || !user) return;
    const url = isOwnPassport
      ? `${window.location.origin}/passport/user/${user.user_id}`
      : window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Passport link copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  const applyStreakSave = async (dateStr) => {
    if (savingStreak) return;
    setSavingStreak(true);
    try {
      const res = await axios.post(
        `${API}/streak/use-save`,
        { date: dateStr },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const label = new Date(dateStr + 'T12:00:00Z').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
      toast.success(`🔥 Flame stays alive — ${label} saved!`);
      // Reload passport to refresh streak + save status
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save streak');
    } finally {
      setSavingStreak(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50" data-testid="passport-loading">
        <div className="text-slate-500">Loading passport...</div>
      </div>
    );
  }

  if (error || !passport) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 p-6" data-testid="passport-error">
        <div className="text-2xl font-black text-slate-700 mb-3">{error || 'Passport unavailable'}</div>
        <button onClick={() => navigate(-1)} className="text-sm text-slate-600 underline">Go back</button>
      </div>
    );
  }

  const { user: owner, stamps, streak, totals, cities, countries, cities_walked } = passport;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="passport-page">
      {/* Header - vintage passport cover style */}
      <div className="bg-gradient-to-br from-amber-900 via-orange-900 to-rose-900 text-amber-50 relative overflow-hidden">
        {/* subtle grain overlay */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 25% 30%, rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '3px 3px',
        }} />
        <div className="relative max-w-5xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="passport-back-btn"
            className="inline-flex items-center gap-2 text-amber-200 hover:text-white text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 text-amber-900 flex items-center justify-center font-black text-2xl border-4 border-amber-200/60 overflow-hidden shadow-xl">
                {owner.avatar_url ? (
                  <img src={owner.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  initials(owner.display_name)
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-1">Audiostride Passport</div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{owner.display_name}</h1>
                {(cities?.length > 0 || countries?.length > 0) && (
                  <div className="text-sm text-amber-200 mt-1 flex items-center gap-1">
                    <Compass className="w-4 h-4" />
                    Explored {cities?.length || 0} {cities?.length === 1 ? 'city' : 'cities'}
                    {countries?.length > 0 && ` · ${countries.length} ${countries.length === 1 ? 'country' : 'countries'}`}
                  </div>
                )}
              </div>
            </div>
            {isOwnPassport && (
              <button
                onClick={handleShare}
                disabled={generating}
                data-testid="passport-share-btn"
                className="inline-flex items-center gap-2 bg-amber-50 text-amber-900 rounded-lg px-4 py-2 text-sm font-black hover:bg-white disabled:opacity-60"
              >
                <Share2 className="w-4 h-4" />
                {generating ? 'Generating…' : 'Share Passport Card'}
              </button>
            )}
          </div>

          {/* Stat strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8" data-testid="passport-stats">
            <div className="bg-amber-950/40 backdrop-blur border border-amber-800/40 rounded-lg p-4">
              <div className="text-amber-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                <BookOpen className="w-3 h-3" /> Stamps
              </div>
              <div className="text-3xl font-black mt-1" data-testid="stat-unique-tours">{totals.unique_tours}</div>
              <div className="text-xs text-amber-200 mt-0.5">unique tours</div>
            </div>
            <div className="bg-amber-950/40 backdrop-blur border border-amber-800/40 rounded-lg p-4">
              <div className="text-amber-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                <Trophy className="w-3 h-3" /> Completions
              </div>
              <div className="text-3xl font-black mt-1" data-testid="stat-total-completions">{totals.total_completions}</div>
              <div className="text-xs text-amber-200 mt-0.5">tours finished</div>
            </div>
            <div className="bg-amber-950/40 backdrop-blur border border-amber-800/40 rounded-lg p-4">
              <div className="text-amber-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                <Globe className="w-3 h-3" /> Cities
              </div>
              <div className="text-3xl font-black mt-1" data-testid="stat-cities">{totals.cities_visited}</div>
              <div className="text-xs text-amber-200 mt-0.5">visited</div>
            </div>
            <div className="bg-amber-950/40 backdrop-blur border border-amber-800/40 rounded-lg p-4">
              <div className="text-amber-300 text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                <Flame className="w-3 h-3" /> Streak
              </div>
              <div className="text-3xl font-black mt-1 flex items-center gap-1" data-testid="stat-streak">
                {streak.current}
                {streak.current > 0 && <Flame className="w-6 h-6 text-orange-400" />}
              </div>
              <div className="text-xs text-amber-200 mt-0.5">
                {streak.current > 0
                  ? `Best ever: ${streak.longest} days`
                  : (streak.longest > 0 ? `Best ever: ${streak.longest} days` : 'Complete a tour to start')}
              </div>
            </div>
          </div>

          {/* Lifetime walked — sums real GPS-tracked distance and walking
              time across every completed tour. A big motivational number
              users love to screenshot when they hit round milestones. */}
          {(totals.lifetime_distance_meters > 0 || totals.lifetime_duration_seconds > 0) && (
            <div
              className="mt-4 bg-gradient-to-r from-orange-800/50 via-amber-900/50 to-orange-800/50 border border-amber-600/40 rounded-xl p-5 shadow-lg"
              data-testid="lifetime-walked-card"
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-amber-500/25 flex items-center justify-center flex-shrink-0 border border-amber-400/40">
                    <Footprints className="w-7 h-7 text-amber-200" />
                  </div>
                  <div>
                    <div className="text-amber-200/80 text-[0.65rem] uppercase tracking-widest font-bold">
                      Lifetime walked with Audiostride
                    </div>
                    <div
                      className="text-3xl sm:text-4xl font-black text-white mt-0.5 leading-none"
                      data-testid="passport-lifetime-distance"
                    >
                      {formatWalkedDistance(totals.lifetime_distance_meters || 0)}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-amber-200/80 text-[0.65rem] uppercase tracking-widest font-bold flex items-center gap-1 justify-end">
                    <Clock className="w-3 h-3" /> Time on foot
                  </div>
                  <div
                    className="text-xl font-black text-white mt-0.5 font-mono"
                    data-testid="passport-lifetime-duration"
                  >
                    {formatWalkedDuration(totals.lifetime_duration_seconds || 0)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cities Explored world map — a small Leaflet mini-map with a pin
          per city walked, radius scaled by distance. Renders itself only
          when we have plottable coords, so it stays hidden for fresh users. */}
      <CitiesExploredMap cities={cities_walked || []} />

      {/* Distance by city — reinforces the traveller/collector narrative.
          Only shows for own passport (public passports focus on stamps).
          Requires at least one city with real walked distance (>0 m) so we
          don't show a table full of "0 m" for legacy tours. */}
      {(cities_walked?.length || 0) > 0 && (cities_walked || []).some((c) => (c.distance_meters || 0) > 0) && (
        <div className="max-w-5xl mx-auto px-6 pt-6" data-testid="cities-walked-section">
          <div className="bg-white rounded-2xl shadow-md border border-amber-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-amber-100 flex items-center gap-2">
              <Globe className="w-5 h-5 text-amber-700" />
              <h2 className="font-black text-slate-900 text-lg">Where you&apos;ve walked</h2>
              <span className="text-xs text-slate-500 ml-auto">
                {cities_walked.length} {cities_walked.length === 1 ? 'city' : 'cities'}
              </span>
            </div>
            <ul className="divide-y divide-amber-50">
              {cities_walked.slice(0, 20).map((row) => {
                // "N tours" now means N distinct tours completed here — the
                // real accomplishment. `completions` is the total number of
                // walks (repeats included), shown as a subtle sub-line so
                // dedicated re-walkers still get credit without inflating
                // the headline number.
                const uniqueTours = row.unique_tours || 0;
                const showRepeats =
                  uniqueTours > 0 && row.completions > uniqueTours;
                return (
                  <li
                    key={row.city}
                    className="px-5 py-3 flex items-center justify-between gap-4"
                    data-testid={`city-walked-row-${row.city}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 truncate">{row.city}</div>
                        <div className="text-xs text-slate-500">
                          {uniqueTours > 0
                            ? `${uniqueTours} ${uniqueTours === 1 ? 'tour' : 'tours'}`
                            : `${row.completions} ${row.completions === 1 ? 'walk' : 'walks'}`}
                          {showRepeats && ` · ${row.completions} walks`}
                          {row.duration_seconds > 0 && ` · ${formatWalkedDuration(row.duration_seconds)}`}
                        </div>
                      </div>
                    </div>
                    <div
                      className="text-right font-black text-slate-900 text-lg tabular-nums flex-shrink-0"
                      data-testid={`city-walked-distance-${row.city}`}
                    >
                      {row.distance_meters > 0 ? formatWalkedDistance(row.distance_meters) : '—'}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Streak Save banner — only on own passport, only when save is available AND a savable gap exists */}
      {isOwnPassport && streak.save?.available && (streak.save.savable_dates || []).length > 0 && (
        <div className="max-w-5xl mx-auto px-6 pt-6" data-testid="streak-save-banner">
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-5 shadow-xl border-2 border-amber-300 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                <Flame className="w-7 h-7 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-black text-white text-lg">Missed a day? Save your streak.</div>
                <div className="text-amber-50 text-sm">
                  You have <strong>1 free save</strong> this month. Restore a day in the last 3.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="streak-save-options">
              {streak.save.savable_dates.map((d) => (
                <button
                  key={d}
                  onClick={() => applyStreakSave(d)}
                  disabled={savingStreak}
                  data-testid={`streak-save-day-${d}`}
                  className="bg-white text-amber-900 font-black text-xs px-3 py-2 rounded-lg shadow hover:bg-amber-50 disabled:opacity-60 flex items-center gap-1"
                >
                  🛟 Save {new Date(d + 'T12:00:00Z').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Streak Save "already used" state — subtle, informative only */}
      {isOwnPassport && streak.save && !streak.save.available && streak.current > 0 && (
        <div className="max-w-5xl mx-auto px-6 pt-6" data-testid="streak-save-used-banner">
          <div className="text-center text-xs text-slate-500 bg-slate-100 rounded-lg py-2 px-4 border border-slate-200">
            🛟 Streak save used for {streak.save.current_month} — next one refreshes on the 1st.
          </div>
        </div>
      )}

      {/* Stamps grid */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        {stamps.length === 0 ? (
          <div className="text-center py-20" data-testid="passport-empty">
            <BookOpen className="w-16 h-16 mx-auto text-amber-400 mb-4" strokeWidth={1.5} />
            <div className="text-xl font-black text-slate-800">No stamps yet</div>
            <p className="text-slate-600 mt-2">
              {isOwnPassport
                ? 'Complete your first tour to earn your first passport stamp.'
                : 'This traveler hasn\u2019t completed a tour yet.'}
            </p>
            {isOwnPassport && (
              <button
                onClick={() => navigate('/')}
                data-testid="passport-cta-explore"
                className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white rounded-lg px-5 py-2.5 text-sm font-black hover:bg-black"
              >
                <Compass className="w-4 h-4" /> Explore tours
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black text-slate-900">Stamps</h2>
              <div className="text-xs text-slate-500 font-bold">{stamps.length} tour{stamps.length === 1 ? '' : 's'}</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="passport-stamps-grid">
              {stamps.map((s) => (
                <div
                  key={s.tour_id}
                  data-testid={`stamp-${s.tour_id}`}
                  className="relative bg-white rounded-xl overflow-hidden border-2 border-amber-200 shadow-md hover:shadow-lg transition-all group cursor-pointer"
                  onClick={() => navigate(`/tour/${s.tour_id}`)}
                >
                  {/* Cover image */}
                  <div className="aspect-video bg-amber-100 relative overflow-hidden">
                    {s.cover_image_url ? (
                      <img
                        src={s.cover_image_url}
                        alt={s.tour_title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-amber-400">
                        <MapPin className="w-12 h-12" strokeWidth={1.5} />
                      </div>
                    )}
                    {/* "Completed" stamp overlay - rotated, vintage feel */}
                    <div
                      className="absolute top-3 right-3 bg-red-600/90 text-white font-black text-[10px] uppercase tracking-widest px-3 py-1 rounded border-2 border-red-100 shadow-lg"
                      style={{ transform: 'rotate(-8deg)' }}
                    >
                      ✓ Completed
                    </div>
                    {s.times_completed > 1 && (
                      <div className="absolute bottom-3 right-3 bg-amber-500 text-white font-black text-xs px-2 py-1 rounded-full shadow-lg">
                        ×{s.times_completed}
                      </div>
                    )}
                  </div>
                  {/* Text */}
                  <div className="p-4">
                    <div className="font-black text-slate-900 line-clamp-2">{s.tour_title}</div>
                    {(s.city || s.country) && (
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {[s.city, s.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="text-xs text-amber-700 font-bold mt-2">
                      First completed {formatDate(s.first_completed_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Passport Card share preview */}
      {cardPreview && (
        <div
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="passport-card-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closeCardPreview(); }}
        >
          <div className="bg-white rounded-2xl overflow-hidden max-w-md w-full shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="font-black text-slate-900">Your Passport Card</div>
              <button
                onClick={closeCardPreview}
                data-testid="passport-card-close-btn"
                className="text-slate-400 hover:text-slate-700 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 p-4 flex items-center justify-center">
              <img
                src={cardPreview.blobUrl}
                alt="Your Audiostride Passport"
                className="max-w-full max-h-[60vh] rounded-lg shadow-lg"
                data-testid="passport-card-image"
              />
            </div>
            <div className="p-4 space-y-2 border-t border-slate-200">
              <button
                onClick={doShare}
                disabled={generating}
                data-testid="passport-card-share-btn"
                className="w-full bg-gradient-to-r from-amber-600 to-orange-700 hover:brightness-110 text-white py-3 rounded-lg font-black flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Share2 className="w-4 h-4" />
                {generating ? 'Preparing…' : 'Share / Download image'}
              </button>
              <button
                onClick={doCopyLink}
                data-testid="passport-card-copy-link-btn"
                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Copy passport link instead
              </button>
              <p className="text-xs text-slate-500 text-center pt-1">
                Tip: On mobile the share sheet lets you post directly to Instagram Stories, WhatsApp, etc.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassportPage;
